import { useState, useEffect } from "react";
import { HiPencil } from "react-icons/hi";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

export default function EditBankPage() {
    const { state } = useLocation(); // bank id from URL
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        bankLogo: "",
        interest: "",
        loanAmount: "",
        phone: "",
        tenure: "",
        monthly_emi: "",
        details: "",
        reward: "",
        recommended: false,
    });

    useEffect(() => {
        setForm(() => ({
            id: state._id || '',
            name: state.name || '',
            bankLogo: state.bankLogo || '',
            interest: state.interest || '',
            phone: state.phone || '',
            loanAmount: state.loanAmount || '',
            tenure: state.tenure || '',
            monthly_emi: state.monthly_emi || '',
            details: state.details || '',
            reward: state.reward || '',
            recommended: state.recommended || false,
        }))
    }, [state]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (name === "bankLogoFile") {
            setForm({ ...form, bankLogoFile: files[0], bankLogo: "" });
            return;
        }

        if (name === "bankLogo") {
            setForm({ ...form, bankLogo: value, bankLogoFile: null });
            return;
        }

        setForm({ ...form, [name]: type === "checkbox" ? checked : value });
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        try {
            const res = await uploadFiles({ fieldName: "file", files: [file] });
            if (res.error) return showErrorToast(res.message || "Failed To Upload Files!");
            setForm(prev => ({ ...prev, bankLogo: res.data.imageUrl }));
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Failed To Upload Image");
        }
    };

    const validateForm = () => {
        if (!form.name) return "Bank name required";
        if (!form.interest) return "Interest required";
        if (!form.loanAmount) return "Loan amount required";
        if (!form.phone) return "Phone number required";
        if (!form.tenure) return "Tenure required";
        if (!form.monthly_emi) return "Monthly EMI required";
        if (!form.details) return "Details required";
        if (!form.bankLogo) return "Please Upload Bank Logo";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateForm();
        if (error) return showErrorToast(error);

        try {
            const res = await makeRequestAuth("post", "/admin/updateLoan", form);
            if (!res.error) {
                showSuccessToast("Bank updated successfully!");
                navigate(-1);
            } else {
                showErrorToast(res.message || "Failed to update bank");
            }
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Server Error");
        }
    };

    return (
        <div className="w-full p-6 space-y-6">
            <ToastContainer />
            <div className="flex items-center mb-6">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="mr-4 flex items-center justify-center w-10 h-10 rounded-xl bg-[#F8F8EC] text-[#434646] shadow hover:bg-[#e9e9dd] transition"
                    title="Go Back"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-6 h-6">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                {/* Title */}
                <div className="flex flex-col">
                    <h2 className="text-3xl font-bold text-[#434646] leading-none">Edit Bank</h2>
                    <p className="text-sm text-[#749EED] mt-1">Update details for this loan provider</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-6 rounded-2xl shadow space-y-6">
                <div className="grid grid-cols-2 gap-5">
                    <div className="col-span-2">
                        <label className="block text-[#434646] font-medium mb-2">Upload Bank Logo</label>
                        <div className="border-2 border-dashed border-[#749EED] rounded-2xl p-4 cursor-pointer hover:border-[#392DFF] transition">
                            <input type="file" name="bankLogo" className="w-full cursor-pointer" onChange={handleFileUpload} />
                        </div>
                        {form.bankLogo && (
                            <div className="flex justify-center mt-4">
                                <div className="w-44 h-44 rounded-2xl overflow-hidden shadow-lg border border-[#e5e5e5] bg-white">
                                    <img src={form.bankLogo} alt="Preview" className="w-full h-full object-contain p-2" />
                                </div>
                            </div>
                        )}
                    </div>

                    <input name="name" placeholder="Bank Name" className="border p-3 rounded-xl" value={form.name} onChange={handleChange} />
                    <input name="interest" placeholder="Interest (8.5%)" className="border p-3 rounded-xl" value={form.interest} onChange={handleChange} />
                    <input name="loanAmount" type="number" placeholder="Loan Amount" className="border p-3 rounded-xl" value={form.loanAmount} onChange={handleChange} />
                    <input name="phone" type="number" placeholder="Phone Number" className="border p-3 rounded-xl" value={form.phone} onChange={handleChange} />
                    <input name="tenure" type="number" placeholder="Tenure (Years)" className="border p-3 rounded-xl" value={form.tenure} onChange={handleChange} />
                    <input name="monthly_emi" type="number" placeholder="Monthly EMI" className="border p-3 rounded-xl" value={form.monthly_emi} onChange={handleChange} />
                    <input name="reward" placeholder="Reward Amount" className="border p-3 rounded-xl" value={form.reward} onChange={handleChange} />
                </div>

                <textarea name="details" placeholder="Loan Details" className="border p-3 rounded-xl w-full min-h-[120px]" value={form.details} onChange={handleChange}></textarea>

                <label className="flex items-center gap-3">
                    <input type="checkbox" name="recommended" checked={form.recommended} onChange={handleChange} />
                    <span className="text-[#434646]">Recommended</span>
                </label>

                <button type="submit" className="bg-[#392DFF] text-white px-5 py-3 rounded-xl flex items-center gap-2 shadow">
                    <HiPencil size={18} /> Update Bank
                </button>
            </form>
        </div>
    );
}
